import classNames from 'classnames'
import React, { forwardRef, useCallback, useEffect, useState } from 'react'
import { WemoloChip } from '../../../components/Wemolo'
import { useDeviceGatewayApi } from '../../../utils/hooks'
import { useLiveViewStateStore } from '../LiveViewStore'
import { FULLSCREEN_WIDTH } from '../LiveViewUI'
import { getCurrentLiveViewSnapshot, regionalAutofocusRequest, setZoomAndFocusValuesRequest } from '../api/requests'
import { useSupportedFunctionalities } from '../hooks'
import { useFocusingRegion } from '../hooks/useFocusingRegion'
import { useLiveStream } from '../hooks/useLiveStream'
import styles from './LiveViewStream.module.css'

const HD_POLL_INTERVAL_MS = 2000

// Separate component for frame status indicator
const FrameStatusIndicator = ({ frameTimeout }: { frameTimeout: number | null }): JSX.Element => {
    let message: string
    let statusClass: string
    if (frameTimeout === null) {
        message = 'Not connected'
        statusClass = styles.error
    } else if (frameTimeout < 3) {
        message = 'Online'
        statusClass = styles.online
    } else if (frameTimeout < 5) {
        message = 'Lagging'
        statusClass = styles.lagging
    } else {
        message = `Last frame ${frameTimeout}s ago`
        statusClass = styles.error
    }

    return (
        <div className={styles.frameStatusIndicator}>
            <WemoloChip label={message} className={classNames(styles.statusChip, statusClass)} />
        </div>
    )
}

export const LiveViewStream = forwardRef<
    HTMLImageElement,
    {
        fullscreen: boolean
        disabled?: boolean
        updateParent?: (ref: HTMLImageElement) => void
    }
>(({ fullscreen, disabled = false, updateParent = (ref: any) => {} }, ref) => {
    const {
        idle,
        baseURL,
        setIsIdle,
        areaFocusEnabled,
        setLoadingZoomFocus,
        loadingZoomFocus,
        hdMode,
        setHdLoading,
        deviceName
    } = useLiveViewStateStore()
    const { imgSrc, loading, error, frameTimeout } = useLiveStream(baseURL as string, false, idle)
    const [snapshotSrc, setSnapshotSrc] = useState<string | null>(null)
    const { handleImageClick, focusingRegionJSX, setIsVisible } = useFocusingRegion(areaFocusEnabled)
    const { IS_REGIONAL_AUTOFOCUS_SUPPORTED } = useSupportedFunctionalities()

    const setSnapshotUrl = useCallback((nextUrl: string | null) => {
        setSnapshotSrc((prev) => {
            if (prev && prev !== nextUrl) URL.revokeObjectURL(prev)
            return nextUrl
        })
    }, [])

    const { data: hdSnapshotBuffer, loading: hdPolling } = useDeviceGatewayApi<ArrayBuffer>({
        baseURL,
        path: '/camera/snapshot',
        method: 'GET',
        binary: true,
        enabled: hdMode,
        intervalMs: HD_POLL_INTERVAL_MS,
        deviceName
    })

    useEffect(() => {
        setHdLoading(hdPolling)
    }, [hdPolling, setHdLoading])

    useEffect(() => {
        if (!hdSnapshotBuffer) return
        const blob = new Blob([hdSnapshotBuffer], { type: 'image/jpeg' })
        setSnapshotUrl(URL.createObjectURL(blob))
    }, [hdSnapshotBuffer, setSnapshotUrl])

    useEffect(() => {
        if (!hdMode) setSnapshotUrl(null)
    }, [hdMode, setSnapshotUrl])

    useEffect(() => {
        let mounted = true
        // Issue with types. Have to manually update parent
        // @ts-ignore
        if (ref?.current && mounted) {
            // @ts-ignore
            updateParent(ref?.current)
        }
        return () => {
            mounted = false
        }
        // @ts-ignore
    }, [ref?.current])

    const handleSnapshot = useCallback(async () => {
        if (!baseURL) return
        try {
            const response = await getCurrentLiveViewSnapshot(baseURL)
            const blob = new Blob([response.data], { type: 'image/jpeg' })
            setSnapshotUrl(URL.createObjectURL(blob))
        } catch (err) {
            console.error('Failed to load snapshot:', err)
        }
    }, [baseURL, setSnapshotUrl])

    const handleMouseLeave = () => {
        if (hdMode) return
        setSnapshotUrl(null)
    }

    if (disabled) {
        return null
    }
    const getRegionalAutofocus = async (normalizedPoints: any) => {
        setLoadingZoomFocus(true)
        setIsVisible(true)
        try {
            const response = await regionalAutofocusRequest(baseURL, normalizedPoints)
            console.log(response)
            try {
                await setZoomAndFocusValuesRequest(baseURL, {
                    zoom: response.data.zoom,
                    focus: response.data.focus,
                    autofocus: false
                })
            } catch (error) {}
            setLoadingZoomFocus(false)
            setIsVisible(false)
        } catch (error) {
            setLoadingZoomFocus(false)
            setIsVisible(false)
        }
    }

    return (
        <div className={styles.root}>
            <div className={styles.streamWrapper}>
                {baseURL && !idle ? (
                    <>
                        {focusingRegionJSX}
                        <img
                            ref={ref}
                            className={styles.streamImage}
                            style={{
                                width: fullscreen ? FULLSCREEN_WIDTH : '100%',
                                cursor: areaFocusEnabled && IS_REGIONAL_AUTOFOCUS_SUPPORTED ? 'pointer' : 'auto'
                            }}
                            src={
                                snapshotSrc
                                    ? snapshotSrc
                                    : loading
                                      ? `https://placehold.co/600x400?text=Loading...`
                                      : error
                                        ? `https://placehold.co/600x400?text=${error}`
                                        : imgSrc
                                          ? imgSrc
                                          : `https://placehold.co/600x400?text=Loading...`
                            }
                            alt="Live Stream"
                            onMouseEnter={(e) => {
                                if (e.shiftKey) handleSnapshot()
                            }}
                            onMouseLeave={handleMouseLeave}
                            onClick={(e) => {
                                if (e.shiftKey) {
                                    handleSnapshot()
                                    return
                                }
                                if (!areaFocusEnabled || loadingZoomFocus) return
                                getRegionalAutofocus(handleImageClick(e))
                            }}
                        />
                        <FrameStatusIndicator frameTimeout={frameTimeout} />
                    </>
                ) : (
                    <div className={styles.idleWrapper} onClick={() => setIsIdle(false)}>
                        <img
                            className={styles.streamImage}
                            style={{ width: fullscreen ? FULLSCREEN_WIDTH : '100%' }}
                            src={`https://placehold.co/600x400?text=Stream+Closed${idle ? ', click to open' : ''}`}
                            alt="Stream Closed"
                        />
                    </div>
                )}
            </div>
        </div>
    )
})
